import { DragDropContext, Draggable, Droppable } from "@hello-pangea/dnd";
import { Button, Checkbox, CircularProgress } from "@mui/material";
import MUITooltip from "@mui/material/Tooltip";
import { styled } from "@mui/material/styles";
import { get, includes } from "lodash";
import { Link } from "react-router-dom";
import { Landvision, Sorty } from "../../../common/js/icons";
import { noPhotoUrl } from "../utils/constants";
import utils, { getFromAttributeValues, navigateToLandvision } from "../utils/operations";
import { NO_CONTENT_PLACEHOLDER } from "./constants";
import schema, { getButtonActionPropertiesForRoleAndStatus } from "./reviewCompsTables";
import { getCompInfo, getLeaseRate, getSalePrice } from "./utils";

import "./Comp.css";

const StyledLightButton = styled(Button)(({ theme }) => ({
    fontWeight: 400,
    minWidth: 0,
    margin: 0,
    marginRight: "5px",
    padding: "5px 10px",
    color: theme.palette.primary.main,
}));

const StyledAlternateContent = styled("div")(() => ({
    margin: "auto",
    textAlign: "center",
    marginTop: "6em",
    marginBottom: "6em",
    color: "rgba(0, 0, 0, 0.30)",
    fontSize: "20px",
}));

const StyledListingIdLink = styled("span")(() => ({
    "&:hover": {
        cursor: "pointer",
        textDecoration: "underline",
    },
    color: "#2C99D2",
    fontWeight: 600,
    display: "-webkit-box",
    WebkitLineClamp: 2,
    WebkitBoxOrient: "vertical",
    overflow: "hidden",
    textOverflow: "ellipsis",
}));

// Converted from Grid to div
const StyledTableWrapper = styled("div")(() => ({
    fontFamily: "IBM Plex Sans, sans-serif",
    overflowX: "auto",
    width: "100%",
}));

// Converted from Grid to div
const StyledTableHeaderRow = styled("div")(() => ({
    display: "flex",
    alignItems: "center",
    borderBottom: "1px solid rgb(212, 212, 212)",
    color: "#9E9E9E",
    fontSize: "12px",
    fontWeight: "bold",
    padding: "1.5em 0",
    overflow: "hidden",
    scrollbarGutter: "stable",
    width: "100%",
}));

// Converted from Grid to div
const StyledTableHeaderCell = styled("div")(({ theme, width }) => ({
    paddingRight: "7px",
    flex: width,
}));

const StyledLb = styled(Link)(() => ({
    textDecoration: "none",
}));

// Converted from Grid to div
const StyledTableBodyRow = styled("div")(() => ({
    display: "flex",
    alignItems: "center",
    borderBottom: "1px solid rgb(212, 212, 212)",
    fontSize: "13px",
    height: "fit-content",
    padding: "0.75em 0",
    width: "100%",
}));

// Converted from Grid to div
const StyledTableBodyCell = styled("div")(({ theme, width }) => ({
    paddingRight: "7px",
    flex: width,
}));

const StyledTableBodyWrapper = styled("div")(() => ({
    display: "block",
    maxHeight: "50vh",
    minHeight: "425px",
    overflowY: "auto",
    scrollbarGutter: "stable",
    width: "100%",
}));

const CompsWithoutMap = (props) => {
    const {
        compTab,
        role,
        handleSort,
        searchCleared,
        searchRun,
        isLoading,
        loadingSearch,
        selectAll,
        selectAllGroup,
        selectAllBrokerComps,
        handleSelectAll,
        handleSelectAllGroup,
        handleSelectAllBrokerComps,
        handleDragEnd,
        selectedComps,
        selectedCompsGroup,
        brokerComps,
        handleCheckboxClick,
        filterQuery,
        isBrokerOnDeal,
    } = props;

    let comps = get(props.comps, compTab) || [];
    const hasComps = Object.keys(comps).length > 0;
    const brokerCompsTab = compTab === "broker_comps";
    const headers = brokerCompsTab ? schema.viewHeaders["broker_comps"] : schema.viewHeaders["all_km_comps"];
    const loadRows = brokerCompsTab ? !isLoading && hasComps : !loadingSearch && !searchCleared && hasComps;

    if (compTab === "broker_comps" && filterQuery) {
        comps = comps.filter((comp) => schema.filterByQuery(comp, schema.columnsToQueryFilter, filterQuery));
    }

    // Helper function to determine cell width based on header name
    const getCellWidth = (headerName) => {
        if (headerName === "Links") return 3;
        if (headerName === "Address") return 2;
        return 1;
    };

    const onDragEnd = (results) => {
        const { destination, source } = results;
        if (!destination) return;
        if (destination.droppableId === source.droppableId && destination.index === source.index) return;
        handleDragEnd(results, comps, compTab);
    };

    return (
        <div>
            <StyledTableWrapper>
                <div style={{ minWidth: "1450px" }}>
                    {/* Table Header */}
                    <StyledTableHeaderRow>
                        {headers.map((header, i) => {
                            const cellWidth = getCellWidth(header.name);

                            return (
                                <StyledTableHeaderCell
                                    key={`gh-c${i}`}
                                    width={cellWidth}
                                    onClick={() => handleSort(header.name, compTab)}
                                >
                                    {header.name === "Property" ? (
                                        <div style={{ display: "flex", alignItems: "center" }}>
                                            {compTab === "comps_group" ? (
                                                <Checkbox
                                                    className={
                                                        selectAllGroup
                                                            ? "compCheckboxAllSelectedMap"
                                                            : "compCheckboxAllMap"
                                                    }
                                                    checked={selectAllGroup}
                                                    value={selectAllGroup}
                                                    onChange={() => handleSelectAllGroup()}
                                                    color="primary"
                                                />
                                            ) : compTab === "broker_comps" ? (
                                                <Checkbox
                                                    className={
                                                        selectAllBrokerComps
                                                            ? "compCheckboxAllSelectedMap"
                                                            : "compCheckboxAllMap"
                                                    }
                                                    checked={selectAllBrokerComps}
                                                    value={selectAllBrokerComps}
                                                    onChange={() => handleSelectAllBrokerComps()}
                                                    color="primary"
                                                />
                                            ) : (
                                                <Checkbox
                                                    className={
                                                        selectAll ? "compCheckboxAllSelectedMap" : "compCheckboxAllMap"
                                                    }
                                                    checked={selectAll}
                                                    value={selectAll}
                                                    onChange={() => handleSelectAll()}
                                                    color="primary"
                                                />
                                            )}
                                            <span style={{ paddingLeft: "7px" }}>{header.name.toUpperCase()}</span>
                                        </div>
                                    ) : header.name === "Links" ? (
                                        <div>{header.name.toUpperCase()}</div>
                                    ) : (
                                        <div style={{ display: "flex", alignItems: "center" }}>
                                            <span style={{ paddingRight: "7px" }}>{header.name.toUpperCase()}</span>
                                            <Sorty />
                                        </div>
                                    )}
                                </StyledTableHeaderCell>
                            );
                        })}
                    </StyledTableHeaderRow>

                    {/* Table Body */}
                    <DragDropContext onDragEnd={onDragEnd}>
                        <Droppable droppableId="droppable" style={{ maxWidth: "100%" }}>
                            {(provided, snapshot) => (
                                <StyledTableBodyWrapper {...provided.droppableProps} ref={provided.innerRef}>
                                    {loadRows
                                        ? comps.map((comp, i) =>
                                              row(
                                                  comp,
                                                  i,
                                                  headers,
                                                  compTab,
                                                  role,
                                                  selectedComps,
                                                  selectedCompsGroup,
                                                  brokerComps,
                                                  handleCheckboxClick,
                                                  isBrokerOnDeal,
                                                  getCellWidth
                                              )
                                          )
                                        : alternateContent(compTab, isLoading, loadingSearch, hasComps, searchRun)}
                                    {provided.placeholder}
                                </StyledTableBodyWrapper>
                            )}
                        </Droppable>
                    </DragDropContext>
                </div>
            </StyledTableWrapper>
        </div>
    );
};

const row = (
    comp,
    i,
    headers,
    compTab,
    role,
    selectedComps,
    selectedCompsGroup,
    brokerComps,
    handleCheckboxClick,
    isBrokerOnDeal,
    getCellWidth
) => {
    const getItemStyle = (isDragging, draggableStyle) => ({
        background: isDragging ? "#C5C5C5" : "",
        userSelect: "none",
        width: "100%",
        ...draggableStyle,
    });

    const buttonProperties = getButtonActionPropertiesForRoleAndStatus(role, comp.calculatedValueB);
    const dealKey = getFromAttributeValues(comp, ["dealkey", "dealKey"]);
    return (
        <Draggable
            draggableId={`AllCompsWMap Draggable - ${String(i)}`}
            index={i}
            style={{ width: "100%", height: "fit-content" }}
            key={`gb-r${i}`}
        >
            {(provided, snapshot) => (
                <div
                    ref={provided.innerRef}
                    {...provided.draggableProps}
                    {...provided.dragHandleProps}
                    style={getItemStyle(snapshot.isDragging, provided.draggableProps.style)}
                >
                    <StyledTableBodyRow>
                        {headers.map((header, j) => {
                            const compPhoto = getFromAttributeValues(
                                comp,
                                ["listing_photo", "property_photo", "listingPhoto", "propertyPhoto"],
                                noPhotoUrl
                            );

                            const cellWidth = getCellWidth(header.name);

                            return (
                                <StyledTableBodyCell key={`gb-r${i}-c${j}`} width={cellWidth}>
                                    {
                                        /* --- Property --- */
                                        header.name === "Property" ? (
                                            <div style={{ display: "flex", alignItems: "center" }}>
                                                {compTab === "comps_group" ? (
                                                    <Checkbox
                                                        className={
                                                            selectedCompsGroup.get(dealKey) === true
                                                                ? "compCheckboxAllSelectedMap"
                                                                : "compCheckboxAllMap"
                                                        }
                                                        checked={selectedCompsGroup.get(dealKey) === true}
                                                        value={selectedCompsGroup.get(dealKey) === true}
                                                        onChange={() => handleCheckboxClick(dealKey, compTab)}
                                                        color="primary"
                                                    />
                                                ) : compTab === "broker_comps" ? (
                                                    <Checkbox
                                                        className={
                                                            brokerComps.get(dealKey) === true
                                                                ? "compCheckboxAllSelectedMap"
                                                                : "compCheckboxAllMap"
                                                        }
                                                        checked={brokerComps.get(dealKey) === true}
                                                        value={brokerComps.get(dealKey) === true}
                                                        onChange={() => handleCheckboxClick(dealKey, compTab)}
                                                        color="primary"
                                                    />
                                                ) : (
                                                    <Checkbox
                                                        className={
                                                            selectedComps.get(dealKey) === true
                                                                ? "compCheckboxAllSelectedMap"
                                                                : "compCheckboxAllMap"
                                                        }
                                                        checked={selectedComps.get(dealKey) === true}
                                                        value={selectedComps.get(dealKey) === true}
                                                        onChange={() => handleCheckboxClick(dealKey, compTab)}
                                                        color="primary"
                                                    />
                                                )}
                                                <div
                                                    className="propertySmallImageContainer"
                                                    style={{
                                                        backgroundImage: `url(${compPhoto})`,
                                                        marginLeft: "7px",
                                                        width: "85px",
                                                        minWidth: "85px",
                                                        height: "85px",
                                                    }}
                                                ></div>
                                            </div>
                                        ) : /* --- Address --- */
                                        header.name === "Address" ? (
                                            <div>
                                                {!getFromAttributeValues(
                                                    comp,
                                                    ["confidentialFlg", "confidential_flg"],
                                                    false
                                                ) ||
                                                includes(["dataEntry", "dataAdmin"], role) ||
                                                isBrokerOnDeal(comp) ? (
                                                    <StyledLb to={`/comp/${dealKey}`} target="_blank">
                                                        <StyledListingIdLink>
                                                            {getFromAttributeValues(
                                                                comp,
                                                                ["listing_name", "listingName"],
                                                                ""
                                                            ).trim()}
                                                        </StyledListingIdLink>
                                                    </StyledLb>
                                                ) : (
                                                    <div style={{ color: "red", fontWeight: "bold" }}>Confidential</div>
                                                )}
                                                <div>{`${get(comp, "address1", {})}`}</div>
                                                <div>{`${get(comp, "city", {})}, ${getFromAttributeValues(
                                                    comp,
                                                    ["state_code", "state"],
                                                    ""
                                                )}, ${getFromAttributeValues(
                                                    comp,
                                                    ["zip_postal_code", "zip"],
                                                    ""
                                                )}`}</div>
                                                <div>{getCompInfo(comp)}</div>
                                            </div>
                                        ) : /* --- Rate / Price --- */
                                        header.name === "Rate/Price" ? (
                                            getFromAttributeValues(
                                                comp,
                                                ["confidentialFlg", "confidential_flg"],
                                                false
                                            ) === true ? (
                                                NO_CONTENT_PLACEHOLDER
                                            ) : (
                                                <div numeric={header.numeric}>
                                                    {
                                                        /* - Lease - */
                                                        utils.parseTransactionType(
                                                            getFromAttributeValues(
                                                                comp,
                                                                ["transaction_type", "transactionType"],
                                                                null
                                                            )
                                                        ) === "Lease"
                                                            ? getLeaseRate(comp)
                                                            : /* - Sale - */
                                                              getSalePrice(comp)
                                                    }
                                                </div>
                                            )
                                        ) : /* --- Has Transform --- */
                                        header.transform ? (
                                            /* - Brokers - */
                                            header.name === "Brokers" ? (
                                                <div>
                                                    {getFromAttributeValues(comp, ["listing_agents", "brokers"], "") ? (
                                                        getFromAttributeValues(
                                                            comp,
                                                            ["listing_agents", "brokers"],
                                                            ""
                                                        ).includes(",") ? (
                                                            <MUITooltip
                                                                title={getFromAttributeValues(comp, [
                                                                    "listing_agents",
                                                                    "brokers",
                                                                ])}
                                                                placement="top"
                                                                sx={{
                                                                    bgcolor: "#333",
                                                                }}
                                                            >
                                                                <span>
                                                                    {" "}
                                                                    {header.transform(
                                                                        getFromAttributeValues(comp, [
                                                                            "listing_agents",
                                                                            "brokers",
                                                                        ])
                                                                    )}
                                                                </span>
                                                            </MUITooltip>
                                                        ) : (
                                                            <div>
                                                                {getFromAttributeValues(
                                                                    comp,
                                                                    ["listing_agents", "brokers"],
                                                                    NO_CONTENT_PLACEHOLDER
                                                                )}
                                                            </div>
                                                        )
                                                    ) : (
                                                        NO_CONTENT_PLACEHOLDER
                                                    )}
                                                </div>
                                            ) : (
                                                <div numeric={header.numeric}>
                                                    {get(comp, header.prop)
                                                        ? header.transform(get(comp, header.prop))
                                                        : NO_CONTENT_PLACEHOLDER}
                                                </div>
                                            )
                                        ) : // End of Has Transform
                                        /* --- Links --- */
                                        header.name === "Links" ? (
                                            <div
                                                style={{ display: "flex", alignItems: "center", whiteSpace: "nowrap" }}
                                            >
                                                <MUITooltip
                                                    title="Landvision"
                                                    placement="top"
                                                    sx={{
                                                        bgcolor: "#333",
                                                    }}
                                                >
                                                    <span>
                                                        <StyledLightButton
                                                            onClick={() =>
                                                                navigateToLandvision(
                                                                    getFromAttributeValues(
                                                                        comp,
                                                                        ["property_id", "propertyKey"],
                                                                        false
                                                                    ),
                                                                    get(comp, "listingkey", null)
                                                                )
                                                            }
                                                            color="primary"
                                                        >
                                                            <Landvision fill="#2C99D2" />
                                                        </StyledLightButton>
                                                    </span>
                                                </MUITooltip>

                                                {/* View Button */}
                                                {/* The view button isn't rendered if the comp is confidential and the user isn't a broker on the comp or dataEntry / dataAdmin */}
                                                {(!getFromAttributeValues(
                                                    comp,
                                                    ["confidentialFlg", "confidential_flg"],
                                                    false
                                                ) ||
                                                    includes(["dataEntry", "dataAdmin"], role) ||
                                                    isBrokerOnDeal(comp) ||
                                                    compTab === "broker_comps") && (
                                                    <StyledLb to={`/comp/${dealKey}`}>
                                                        <Button
                                                            variant={buttonProperties.variant}
                                                            color="primary"
                                                            onClick={() => null}
                                                            style={{ marginLeft: "50px" }}
                                                        >
                                                            {buttonProperties.text}
                                                        </Button>
                                                    </StyledLb>
                                                )}
                                            </div>
                                        ) : (
                                            <div>
                                                {get(comp, header.prop)
                                                    ? get(comp, header.prop)
                                                    : NO_CONTENT_PLACEHOLDER}
                                            </div>
                                        )
                                    }
                                </StyledTableBodyCell>
                            );
                        })}
                    </StyledTableBodyRow>
                </div>
            )}
        </Draggable>
    );
};

const alternateContent = (compTab, isLoading, loadingSearch, hasComps, searchRun) => {
    return (
        <div style={{ width: "100%" }}>
            <div style={{ width: "100%" }}>
                {compTab === "all_km_comps" &&
                    (loadingSearch ? (
                        <StyledAlternateContent>
                            <CircularProgress color="primary" />
                        </StyledAlternateContent>
                    ) : !hasComps ? (
                        searchRun ? (
                            <StyledAlternateContent>No Comps</StyledAlternateContent>
                        ) : (
                            <StyledAlternateContent>Please apply search criteria</StyledAlternateContent>
                        )
                    ) : null)}

                {(compTab === "comps_group" || compTab === "broker_comps") &&
                    (loadingSearch || isLoading ? (
                        <StyledAlternateContent>
                            <CircularProgress color="primary" />
                        </StyledAlternateContent>
                    ) : !hasComps ? (
                        <StyledAlternateContent>No Comps</StyledAlternateContent>
                    ) : null)}
            </div>
        </div>
    );
};

export default CompsWithoutMap;
